import { useCallback, useEffect, useState } from "react";
import { AccessProvider, useAccess } from "./api/access";
import { api } from "./api/client";
import type { Shot } from "./api/types";
import { useWebSocket } from "./api/useWebSocket";
import { wsBus } from "./api/wsBus";
import { Bell } from "./components/Bell";
import { Logo } from "./components/Logo";
import { useRoute, type Route } from "./router";
import { AuditScreen } from "./screens/AuditScreen";
import { CalendarScreen } from "./screens/CalendarScreen";
import { DocumentsScreen } from "./screens/DocumentsScreen";
import { RecentScreen } from "./screens/RecentScreen";
import { ForumScreen } from "./screens/ForumScreen";
import { ScheduleScreen } from "./screens/ScheduleScreen";
import { UsersScreen } from "./screens/UsersScreen";

interface AppProps {
  username: string;
  authEnabled: boolean;
  /** 1 camera viewer, 2 team member, 3 admin. The single source for gating. */
  accessLevel: number;
  onLogout: () => void;
}

/**
 * The provider has to sit above everything that gates on a level, so the shell
 * below is a separate component: a component cannot consume a context it
 * renders itself.
 */
export function App(props: AppProps) {
  return (
    <AccessProvider level={props.accessLevel}>
      <AppShell {...props} />
    </AccessProvider>
  );
}

function AppShell({ username, authEnabled, onLogout }: AppProps) {
  const { state, go, hashFor } = useRoute();
  const { connected } = useWebSocket();
  const { canUseSchedule, isAdmin } = useAccess();

  // The bell badge is global: it accumulates new shots regardless of which
  // screen is open. Each screen subscribes to wsBus independently to refresh
  // its own data.
  const [newShots, setNewShots] = useState<Shot[]>([]);
  const newIds = new Set(newShots.map((s) => s.imageId));

  // Unanswered Events. Distinct from the bell, which keeps meaning "new
  // photos" — a hunt on Saturday and a moose at the salt lick are different
  // kinds of news and must not share one badge.
  const [pendingEvents, setPendingEvents] = useState(0);

  useEffect(() => {
    return wsBus.subscribe(async (evt) => {
      if (evt.type !== "image.uploaded") return;
      try {
        const shot = await api.getImage(evt.imageId);
        setNewShots((prev) => {
          if (prev.some((s) => s.imageId === shot.imageId)) return prev;
          return [shot, ...prev].slice(0, 12);
        });
      } catch {
        // Row not yet visible — let the next event reconcile.
      }
    });
  }, []);

  // Events do not arrive over the WebSocket — nobody needs to know within the
  // second that a hunt was scheduled — so the count is read on mount and
  // whenever the Schedule screen is left, which is when it can have changed.
  const refreshPending = useCallback(async () => {
    if (!canUseSchedule) return;
    try {
      const { count } = await api.pendingEventCount();
      setPendingEvents(count);
    } catch {
      // A badge is not worth surfacing an error for; it reappears next load.
    }
  }, [canUseSchedule]);

  useEffect(() => {
    void refreshPending();
  }, [refreshPending, state.route]);

  const markAllSeen = useCallback(() => setNewShots([]), []);
  const onBellClickShot = useCallback(
    (shot: Shot) => {
      go("calendar", { date: new Date(shot.capturedAt), imageId: shot.imageId });
    },
    [go],
  );
  const bellHrefForShot = useCallback(
    (shot: Shot) =>
      hashFor("calendar", { date: new Date(shot.capturedAt), imageId: shot.imageId }),
    [hashFor],
  );
  const onJumpToCalendar = useCallback((d: Date) => go("calendar", { date: d }), [go]);

  // Navigation audit beacon. Keyed off router state rather than the go()
  // callback so it also catches back/forward and deep links — those are
  // navigations too, and a trail that missed them would be misleading.
  // selectedDate only matters on the calendar, so it is excluded from the
  // dependency list elsewhere to avoid a beacon per clock tick.
  const navKey =
    state.route === "calendar"
      ? `calendar|${formatDay(state.selectedDate)}|${state.focusImageId ?? ""}`
      : state.route;
  useEffect(() => {
    const [route, date, imageId] = navKey.split("|");
    api.recordNav(route, { date: date || undefined, imageId: imageId || null });
  }, [navKey]);

  return (
    <>
      <header className="hdr">
        <div className="hdr-inner">
          <div className="brand">
            <span className="mark" style={{ color: "var(--fg)" }}>
              <Logo />
            </span>
            <span>
              Buaforsälgar<sup>v2</sup>
            </span>
          </div>

          {/*
            Two groups, because there are two subjects here and they were
            previously one undifferentiated row of five. Everything on the left
            is about photographs the cameras took; everything on the right is
            about the people. "Calendar" and "Schedule" sat side by side looking
            like variants of one another when they are the clearest example of
            the distinction CONTEXT.md insists on.

            Each group is its own <nav> with its own accessible name, so the
            grouping is not merely visual — the labels are what a screen reader
            gets instead of the pill divider.
          */}
          <div className="nav-groups">
            <nav className="nav nav-group" aria-label="Trail cams">
              <span className="nav-group-label" aria-hidden="true">
                Trail cams
              </span>
              <NavButton route="recent" current={state.route} onGo={go} label="Recent">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
              </NavButton>

              <NavButton route="calendar" current={state.route} onGo={go} label="Calendar">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </NavButton>
            </nav>

            {/* Hidden entirely from a camera viewer rather than shown disabled:
                every item in it is level 2 or above, so an empty labelled group
                would only advertise a door they cannot open. */}
            {canUseSchedule && (
              <nav className="nav nav-group" aria-label="Hunting team">
                <span className="nav-group-label" aria-hidden="true">
                  Hunting team
                </span>
                <NavButton
                  route="schedule"
                  current={state.route}
                  onGo={go}
                  label="Schedule"
                  badge={pendingEvents}
                >
                  <path d="M8 2v4M16 2v4" />
                  <rect x="3" y="6" width="18" height="16" rx="2" />
                  <path d="M8 13l2.5 2.5L16 10" />
                </NavButton>

                <NavButton route="forum" current={state.route} onGo={go} label="Forum">
                  <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8c-1.5 3-4.7 4.7-8.1 4.2L7 21l1.2-3.3A8 8 0 0 1 12 3.5a8.5 8.5 0 0 1 9 8z" />
                  <path d="M9 10h6M9 13.5h4" />
                </NavButton>

                {/* A folder, not a page: the distinction from the Forum is that
                    this is a filing cabinet and that is a conversation. */}
                <NavButton route="documents" current={state.route} onGo={go} label="Documents">
                  <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h9a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
                </NavButton>

                {isAdmin && (
                  <NavButton route="audit" current={state.route} onGo={go} label="Audit">
                    <path d="M4 4h11l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
                    <path d="M14 4v6h6" />
                    <path d="M8 14h6M8 17h4" />
                  </NavButton>
                )}

                {isAdmin && (
                  <NavButton route="users" current={state.route} onGo={go} label="People">
                    <circle cx="9" cy="8" r="3.2" />
                    <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
                    <path d="M17 11a3 3 0 1 0 0-6M18 20c0-2.5-1-4.3-2.5-5.3" />
                  </NavButton>
                )}
              </nav>
            )}
          </div>

          <div className="hdr-right">
            <span
              className="eyebrow"
              style={{ color: connected ? "var(--moss)" : "var(--fg-mute)" }}
              title={connected ? "Live" : "Reconnecting…"}
              aria-live="polite"
            >
              {connected ? "Live" : "Offline"}
            </span>
            <Bell
              newShots={newShots}
              onMarkSeen={markAllSeen}
              onClickShot={onBellClickShot}
              hrefForShot={bellHrefForShot}
            />
            {authEnabled && (
              <div className="user-menu">
                <span className="user-name" title={username}>
                  {username}
                </span>
                <button
                  className="user-logout"
                  type="button"
                  onClick={onLogout}
                  aria-label="Sign out"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="shell">
        {/* Deep-linking below your level lands on Recent rather than on a screen
            whose every request 403s. Each endpoint is gated server-side too. */}
        {state.route === "audit" && isAdmin ? (
          <AuditScreen />
        ) : state.route === "users" && isAdmin ? (
          <UsersScreen />
        ) : state.route === "schedule" && canUseSchedule ? (
          <ScheduleScreen onPendingChanged={refreshPending} />
        ) : state.route === "forum" && canUseSchedule ? (
          <ForumScreen />
        ) : state.route === "documents" && canUseSchedule ? (
          <DocumentsScreen />
        ) : state.route === "calendar" ? (
          <CalendarScreen
            selectedDate={state.selectedDate}
            focusImageId={state.focusImageId}
            onSelectDate={(d) => go("calendar", { date: d })}
            onFocusConsumed={() => go("calendar", { date: state.selectedDate })}
          />
        ) : (
          <RecentScreen
            newIds={newIds}
            onJumpToCalendar={onJumpToCalendar}
            pendingEvents={canUseSchedule ? pendingEvents : 0}
            onOpenSchedule={() => go("schedule")}
          />
        )}
      </div>
    </>
  );
}

/**
 * One nav item. Extracted once the count reached five: the badge and the
 * aria-current wiring are the same every time, and five copies of it is five
 * chances for one to drift.
 */
function NavButton({
  route,
  current,
  onGo,
  label,
  badge,
  children,
}: {
  route: Route;
  current: Route;
  onGo: (r: Route) => void;
  label: string;
  badge?: number;
  children: React.ReactNode;
}) {
  const active = current === route;
  return (
    <button
      className={active ? "active" : ""}
      onClick={() => onGo(route)}
      aria-current={active ? "page" : undefined}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        {children}
      </svg>
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="nav-badge" aria-label={`${badge} awaiting your answer`}>
          {badge}
        </span>
      )}
    </button>
  );
}

/** Local calendar parts, matching the router's URL format. */
function formatDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
